import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import * as turf from '@turf/turf';
import { IValorGeojsonForm } from '../map-draw-mapbox/geometry-mapper';

/** Resultado de búsqueda de Nominatim con polygon_geojson=1. */
export interface IOsmResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  addresstype?: string;
  geojson?: {
    type: string;
    coordinates: any;
  };
}

/**
 * Búsqueda de límites administrativos en OpenStreetMap (Nominatim).
 * Reemplaza el proceso manual: OSM → relation ID → polygons.openstreetmap.fr
 * → adaptar GeoJSON → importador. Acá: buscás por nombre y listo.
 *
 * Usa HttpClient crudo (NO HttpClientService): la URL es absoluta a Nominatim,
 * así no se le prefija la base ni se le adjuntan headers de auth de la API.
 *
 * La búsqueda es mundial: no se restringe por país (a diferencia del
 * autocompletado de direcciones, que sí filtra por PAIS). Uso esporádico
 * (alta de ubicaciones) → dentro de la política de uso de Nominatim.
 */
@Injectable({ providedIn: 'root' })
export class OsmService {
  private http = inject(HttpClient);

  public buscar(texto: string): Observable<IOsmResult[]> {
    const params = new HttpParams()
      .set('q', texto)
      .set('format', 'jsonv2')
      .set('polygon_geojson', '1')
      .set('limit', '5')
      .set('accept-language', 'es');
    return this.http.get<IOsmResult[]>(
      'https://nominatim.openstreetmap.org/search',
      { params },
    );
  }

  // Tope duro de vértices tras simplificar (evita "query is too large" en el
  // $geoWithin de MongoDB al guardar)
  private readonly MAX_PUNTOS = 400;
  // Tolerancia de simplificación como fracción del lado del bbox: al ser
  // proporcional al tamaño, una ciudad chica conserva detalle (más puntos) y
  // un país se generaliza (menos puntos) sin dejar de parecerse a su silueta
  private readonly RATIO_TOLERANCIA = 0.0006;
  // Descarta partes de un MultiPolygon con área < 0.5% de la parte mayor
  // (islas/enclaves ínfimos que solo aportan puntos y no se reconocen)
  private readonly UMBRAL_ISLA = 0.005;
  // Máximo de partes (polígonos) a conservar de un MultiPolygon
  private readonly MAX_PARTES = 25;

  /**
   * GeoJSON de Nominatim → valor del form del mapa ({ type, coordinates }).
   * Conserva solo los anillos exteriores (sin agujeros, igual que el resto
   * del sistema), descarta islas ínfimas y simplifica para que no dispare el
   * error "query is too large" al guardar sin perder la silueta. Devuelve
   * null si el resultado no es un límite administrativo (punto o línea).
   */
  public toValor(geojson?: {
    type: string;
    coordinates: any;
  }): IValorGeojsonForm | null {
    const valor = this.normalizar(geojson);
    if (!valor) return null;
    return this.simplificar(this.descartarIslas(valor));
  }

  /** Extrae anillos exteriores y normaliza el tipo (Polygon/MultiPolygon). */
  private normalizar(geojson?: {
    type: string;
    coordinates: any;
  }): IValorGeojsonForm | null {
    if (!geojson) return null;

    if (geojson.type === 'Polygon') {
      const exterior = geojson.coordinates?.[0];
      if (!exterior?.length) return null;
      return { type: 'Polygon', coordinates: [exterior], radius: null };
    }

    if (geojson.type === 'MultiPolygon') {
      const exteriores = ((geojson.coordinates as any[]) || [])
        .map((poly) => poly?.[0])
        .filter((anillo) => anillo?.length);
      if (!exteriores.length) return null;
      if (exteriores.length === 1) {
        return { type: 'Polygon', coordinates: [exteriores[0]], radius: null };
      }
      return {
        type: 'MultiPolygon',
        coordinates: exteriores.map((anillo) => [anillo]),
        radius: null,
      };
    }

    // Punto o línea: no es un límite administrativo dibujable
    return null;
  }

  /**
   * Descarta las partes ínfimas de un MultiPolygon (islas/enclaves con área
   * despreciable frente a la mayor). Sin esto, al simplificar los pocos puntos
   * del presupuesto se reparten entre cientos de islitas y la parte principal
   * (el continente/territorio) queda irreconocible.
   */
  private descartarIslas(valor: IValorGeojsonForm): IValorGeojsonForm {
    if (valor.type !== 'MultiPolygon') return valor;

    const partes = valor.coordinates as number[][][][]; // [poli][anillo][pt][xy]
    const conArea = partes
      .map((poli) => ({ poli, area: this.areaAnillo(poli?.[0]) }))
      .sort((a, b) => b.area - a.area);

    const areaMayor = conArea[0]?.area ?? 0;
    const conservadas = conArea
      .filter((x) => areaMayor === 0 || x.area >= areaMayor * this.UMBRAL_ISLA)
      .slice(0, this.MAX_PARTES)
      .map((x) => x.poli);

    if (conservadas.length <= 1) {
      return {
        type: 'Polygon',
        coordinates: conservadas[0] ?? partes[0],
        radius: null,
      };
    }
    return { type: 'MultiPolygon', coordinates: conservadas, radius: null };
  }

  /** Área (m²) del anillo; 0 si no es un anillo válido. */
  private areaAnillo(anillo?: number[][]): number {
    if (!anillo || anillo.length < 4) return 0;
    try {
      return turf.area(turf.polygon([anillo]));
    } catch {
      return 0;
    }
  }

  /**
   * Simplifica con tolerancia proporcional al tamaño del territorio (preserva
   * la silueta a cualquier escala) y, de ser necesario, la agranda hasta
   * respetar el tope duro de vértices. Un territorio chico conserva más
   * detalle; uno grande se generaliza, pero sin dejar de parecerse.
   */
  private simplificar(valor: IValorGeojsonForm): IValorGeojsonForm {
    const feature = turf.feature({
      type: valor.type,
      coordinates: valor.coordinates,
    } as GeoJSON.Geometry);

    const [minX, minY, maxX, maxY] = turf.bbox(feature);
    const span = Math.max(maxX - minX, maxY - minY) || 0.01;
    let tolerancia = span * this.RATIO_TOLERANCIA;

    let resultado = valor;
    for (let i = 0; i < 40; i++) {
      const simplificado = turf.simplify(feature, {
        tolerance: tolerancia,
        highQuality: true,
        mutate: false,
      });
      const geom = simplificado.geometry as {
        type: string;
        coordinates: unknown;
      };
      resultado = {
        type: geom.type,
        coordinates: geom.coordinates,
        radius: null,
      };
      if (this.contarPuntos(resultado) <= this.MAX_PUNTOS) break;
      tolerancia *= 1.4;
    }
    return resultado;
  }

  /** Suma de vértices de todos los anillos (Polygon o MultiPolygon). */
  private contarPuntos(valor: IValorGeojsonForm): number {
    const coords = valor.coordinates as number[][][] | number[][][][];
    const anillos =
      valor.type === 'Polygon'
        ? (coords as number[][][])
        : (coords as number[][][][]).flat();
    return anillos.reduce((n, anillo) => n + (anillo?.length ?? 0), 0);
  }
}
